import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { UI_STRINGS } from "../strings/uiStrings";
import { GAME_CONFIG } from "../config/gameConfig";

interface ReadyScreenProps {
  /** The key the local player must press — shown large on screen */
  readyKey: string;
  /** Fired once when the local player presses their assigned key */
  onPlayerReady: () => void;
  /** How many players have confirmed ready (0, 1, or 2) */
  readyCount: number;
}

/**
 * ReadyScreen — shown before the countdown begins.
 *
 * Displays the random key the local player must press to confirm readiness.
 * Listens for the keydown event once, fires onPlayerReady, then removes itself.
 * The server drives readyCount; once it reaches 2 the parent transitions to countdown.
 */
export function ReadyScreen({
  readyKey,
  onPlayerReady,
  readyCount,
}: ReadyScreenProps): React.ReactElement {
  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key.toLowerCase() === readyKey.toLowerCase()) {
        onPlayerReady();
        window.removeEventListener("keydown", handleKeyDown);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [readyKey, onPlayerReady]);

  const localConfirmed = hasConfirmedRef.current;

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-8 pointer-events-none select-none z-30">
      {/* Instruction text */}
      <motion.p
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="text-white/90 text-sm font-bold tracking-[0.2em] uppercase bg-blue-500 px-3 py-2 rounded-md"
      >
        {UI_STRINGS.readyScreen.instruction}
      </motion.p>

      {/* Key display — pulsing glow to draw attention */}
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
        style={{ position: "relative" }}
      >
        {/* Outer glow ring — pulses continuously */}
        <motion.div
          animate={
            localConfirmed
              ? { scale: 1.6, opacity: 0 }
              : {
                  scale: [1, 1.18, 1],
                  opacity: [0.35, 0.6, 0.35],
                }
          }
          transition={
            localConfirmed
              ? { duration: 0.5, ease: "easeOut" }
              : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
          }
          style={{
            position: "absolute",
            inset: -16,
            borderRadius: 20,
            background: localConfirmed
              ? GAME_CONFIG.colors.roundWin
              : GAME_CONFIG.colors.you,
            filter: "blur(18px)",
          }}
        />

        {/* Key box */}
        <motion.div
          animate={
            localConfirmed
              ? { borderColor: GAME_CONFIG.colors.roundWin, scale: 1.08 }
              : {}
          }
          transition={{ duration: 0.25 }}
          style={{
            position: "relative",
            minWidth: 110,
            minHeight: 110,
            borderRadius: 16,
            border: `3px solid ${localConfirmed ? GAME_CONFIG.colors.roundWin : GAME_CONFIG.colors.you}`,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 32px ${GAME_CONFIG.colors.you}55`,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-game)",
              fontSize: "4.5rem",
              fontWeight: 900,
              fontStyle: "italic",
              color: localConfirmed ? GAME_CONFIG.colors.roundWin : "white",
              lineHeight: 1,
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              transition: "color 0.25s",
            }}
          >
            {readyKey.toUpperCase()}
          </span>
        </motion.div>
      </motion.div>

      {/* Ready count indicator */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
        className="flex flex-col items-center gap-2"
      >
        <span className="text-white/50 text-xs font-bold tracking-[0.25em] uppercase">
          {UI_STRINGS.readyScreen.readyCount(readyCount)}
        </span>

        {/* Dot indicators */}
        <div className="flex gap-3">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              animate={
                i < readyCount
                  ? { scale: 1, background: GAME_CONFIG.colors.roundWin }
                  : { scale: 1, background: "rgba(255,255,255,0.15)" }
              }
              transition={{ type: "spring", stiffness: 500, damping: 20 }}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                boxShadow:
                  i < readyCount
                    ? `0 0 8px ${GAME_CONFIG.colors.roundWin}`
                    : "none",
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}
